import axios from "axios";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Briefcase,
  Users,
  Send,
  TrendingUp,
  Plus,
  ArrowRight,
  ArrowUpRight,
  Sparkles,
  Award,
  Target,
  Activity,
  BarChart3,
  CheckCircle2,
  Clock,
  Trophy,
} from "lucide-react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { API, useAuth } from "@/context/AuthContext";
import StatusBadge from "@/components/StatusBadge";
import ScoreIndicator from "@/components/ScoreIndicator";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import SourceBreakdownPanel from "@/components/SourceBreakdownPanel";
import PendingConfirmationsPanel from "@/components/PendingConfirmationsPanel";

// ── stat cards: each one with its own colour theme ──
const STAT_THEMES = {
  blue: {
    bg: "bg-gradient-to-br from-blue-500 to-blue-700",
    chip: "bg-white/20",
    text: "text-white",
    sub: "text-blue-100",
  },
  emerald: {
    bg: "bg-gradient-to-br from-emerald-500 to-emerald-700",
    chip: "bg-white/20",
    text: "text-white",
    sub: "text-emerald-100",
  },
  violet: {
    bg: "bg-gradient-to-br from-violet-500 to-indigo-700",
    chip: "bg-white/20",
    text: "text-white",
    sub: "text-violet-100",
  },
  amber: {
    bg: "bg-gradient-to-br from-amber-500 to-orange-600",
    chip: "bg-white/20",
    text: "text-white",
    sub: "text-amber-100",
  },
};

const StatCard = ({ icon: Icon, label, value, hint, theme = "blue", testId, suffix }) => {
  const t = STAT_THEMES[theme] || STAT_THEMES.blue;
  return (
    <div
      data-testid={testId}
      className={`relative overflow-hidden rounded-xl shadow-sm ring-1 ring-black/5 ${t.bg} ${t.text} p-5`}
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-10 -top-10 w-40 h-40 rounded-full bg-white/10"
      />
      <div className="relative flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className={`text-[11px] uppercase tracking-[0.14em] font-semibold ${t.sub} mb-2`}>
            {label}
          </div>
          <div className="heading text-3xl font-bold tabular-nums leading-none">
            {value}
            {suffix && <span className="text-xl font-semibold opacity-80 ml-0.5">{suffix}</span>}
          </div>
          {hint && <div className={`text-[11px] mt-2 ${t.sub}`}>{hint}</div>}
        </div>
        <div className={`shrink-0 w-10 h-10 rounded-lg ${t.chip} backdrop-blur flex items-center justify-center`}>
          <Icon className="w-5 h-5" strokeWidth={2.2} />
        </div>
      </div>
    </div>
  );
};

const STATUS_LABELS = {
  pendente: "Pendentes",
  analisado: "Analisados",
  contatado: "Contatados",
  em_entrevista: "Em entrevista",
  aprovado: "Aprovados",
  rejeitado: "Rejeitados",
};

const STATUS_COLORS = {
  pendente: "#94a3b8",
  analisado: "#1d70d6",
  contatado: "#0ea5e9",
  em_entrevista: "#a855f7",
  aprovado: "#10b981",
  rejeitado: "#ef4444",
};

const SCORE_COLORS = {
  alto: "#10b981",
  medio: "#f59e0b",
  baixo: "#ef4444",
};

const formatDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" });
  } catch {
    return iso;
  }
};

const ActivityChart = ({ data }) => {
  if (!data?.length) return null;
  const series = data.map((d) => ({ ...d, label: formatDate(d.date) }));
  return (
    <div className="h-44 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={series} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
          <defs>
            <linearGradient id="dashAreaFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#1d70d6" stopOpacity={0.45} />
              <stop offset="100%" stopColor="#1d70d6" stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke="#eef2f7" strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 11, fill: "#64748b" }}
            tickLine={false}
            axisLine={false}
            interval="preserveStartEnd"
          />
          <YAxis
            tick={{ fontSize: 11, fill: "#94a3b8" }}
            tickLine={false}
            axisLine={false}
            allowDecimals={false}
            width={28}
          />
          <Tooltip
            contentStyle={{
              border: "1px solid #e2e8f0",
              borderRadius: 8,
              fontSize: 12,
              padding: "6px 10px",
            }}
            labelStyle={{ color: "#475569", fontWeight: 600 }}
            formatter={(v) => [`${v} candidato${v === 1 ? "" : "s"}`, ""]}
            separator=""
          />
          <Area
            type="monotone"
            dataKey="count"
            stroke="#1d70d6"
            strokeWidth={2.2}
            fill="url(#dashAreaFill)"
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

const StatusDonut = ({ data }) => {
  const filtered = (data || []).filter((d) => d.count > 0);
  const total = filtered.reduce((s, d) => s + d.count, 0);
  if (!total) {
    return (
      <div className="flex flex-col items-center justify-center h-44 text-sm text-slate-400 gap-2">
        <BarChart3 className="w-7 h-7 text-slate-300" />
        Sem candidatos para distribuir
      </div>
    );
  }
  return (
    <div className="flex items-center gap-4">
      <div className="relative w-32 h-32 shrink-0">
        <ResponsiveContainer>
          <PieChart>
            <Pie
              data={filtered}
              dataKey="count"
              nameKey="status"
              innerRadius={42}
              outerRadius={62}
              startAngle={90}
              endAngle={-270}
              paddingAngle={2}
              stroke="none"
            >
              {filtered.map((entry) => (
                <Cell key={entry.status} fill={STATUS_COLORS[entry.status] || "#64748b"} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
          <div className="heading text-2xl font-bold text-slate-900 leading-none tabular-nums">
            {total}
          </div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-1">total</div>
        </div>
      </div>
      <ul className="flex-1 space-y-1.5">
        {filtered.map((d) => (
          <li key={d.status} className="flex items-center gap-2 text-xs">
            <span
              className="w-2.5 h-2.5 rounded-full shrink-0"
              style={{ backgroundColor: STATUS_COLORS[d.status] || "#64748b" }}
            />
            <span className="flex-1 text-slate-700">{STATUS_LABELS[d.status] || d.status}</span>
            <span className="font-semibold text-slate-900 tabular-nums">{d.count}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const ScoreBars = ({ data }) => {
  const max = Math.max(1, ...(data || []).map((d) => d.count));
  if (!data?.length) return null;
  return (
    <div className="space-y-3">
      {data.map((d) => {
        const pct = (d.count / max) * 100;
        return (
          <div key={d.key}>
            <div className="flex items-center justify-between text-xs text-slate-700 mb-1">
              <span className="font-medium">{d.label}</span>
              <span className="tabular-nums font-semibold text-slate-900">{d.count}</span>
            </div>
            <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden">
              <div
                className="h-full rounded-full transition-all"
                style={{
                  width: `${pct}%`,
                  backgroundColor: SCORE_COLORS[d.key] || "#64748b",
                }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

const DashboardPage = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useDocumentTitle(
    user?.name ? `Painel de ${user.name.split(" ")[0]}` : "Dashboard",
  );

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/dashboard/overview`);
        setData(res.data);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const conversion =
    data && data.total_candidates > 0
      ? Math.round((data.contacted / data.total_candidates) * 100)
      : 0;

  return (
    <div data-testid="dashboard-page" className="space-y-7">
      {/* Hero header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-blue-700 via-indigo-700 to-emerald-600 text-white p-6 sm:p-8 shadow-md">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              "radial-gradient(circle at 15% 20%, rgba(255,255,255,0.18), transparent 55%), radial-gradient(circle at 85% 75%, rgba(16,185,129,0.35), transparent 50%)",
          }}
        />
        <div className="relative flex flex-col sm:flex-row sm:items-end sm:justify-between gap-5">
          <div>
            <div className="inline-flex items-center gap-1.5 mb-3 px-2.5 py-1 rounded-full bg-white/15 backdrop-blur text-[11px] font-medium text-white border border-white/20">
              <Sparkles className="w-3 h-3" />
              Painel da {user?.company_name || "empresa"}
            </div>
            <h1 className="heading text-3xl sm:text-4xl font-bold tracking-tight leading-tight">
              Olá, {user?.name?.split(" ")[0] || "Recrutador"} 👋
            </h1>
            <p className="text-sm sm:text-base text-white/85 mt-2 max-w-xl">
              Aqui está o resumo do seu recrutamento. Tudo o que a IA analisou,
              filtrado para a sua visão.
            </p>
          </div>
          <Link
            to="/vagas/nova"
            data-testid="dashboard-new-job-button"
            className="inline-flex items-center gap-2 bg-white text-brand-blue hover:bg-blue-50 px-4 py-2.5 rounded-md text-sm font-semibold transition-colors shadow-sm self-start sm:self-end"
          >
            <Plus className="w-4 h-4" />
            Nova vaga
          </Link>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={Briefcase}
          label="Vagas ativas"
          value={loading ? "—" : data?.active_jobs ?? 0}
          hint={`${data?.total_jobs ?? 0} no total`}
          theme="blue"
          testId="stat-active-jobs"
        />
        <StatCard
          icon={Users}
          label="Currículos analisados"
          value={loading ? "—" : data?.total_candidates ?? 0}
          theme="violet"
          testId="stat-total-candidates"
        />
        <StatCard
          icon={Target}
          label="Score médio de match"
          value={loading ? "—" : data?.avg_score ?? 0}
          suffix="%"
          theme="amber"
          testId="stat-avg-score"
        />
        <StatCard
          icon={Send}
          label="Candidatos contatados"
          value={loading ? "—" : data?.contacted ?? 0}
          hint={`Taxa: ${conversion}%`}
          theme="emerald"
          testId="stat-contacted"
        />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Activity area chart */}
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-lg bg-blue-50 text-brand-blue flex items-center justify-center">
                <Activity className="w-4.5 h-4.5" strokeWidth={2.2} />
              </div>
              <div>
                <h2 className="heading text-base font-semibold text-slate-900">
                  Atividade recente
                </h2>
                <p className="text-xs text-slate-500">
                  Currículos recebidos nos últimos 14 dias
                </p>
              </div>
            </div>
            <span className="hidden sm:inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full">
              <ArrowUpRight className="w-3 h-3" /> tempo real
            </span>
          </div>
          {loading ? (
            <div className="h-44 flex items-center justify-center text-sm text-slate-400">
              Carregando…
            </div>
          ) : (
            <ActivityChart data={data?.daily_activity || []} />
          )}
        </div>

        {/* Status donut */}
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
          <div className="flex items-center gap-2.5 mb-4">
            <div className="w-9 h-9 rounded-lg bg-violet-50 text-violet-700 flex items-center justify-center">
              <BarChart3 className="w-4.5 h-4.5" strokeWidth={2.2} />
            </div>
            <div>
              <h2 className="heading text-base font-semibold text-slate-900">Pipeline</h2>
              <p className="text-xs text-slate-500">Distribuição por status</p>
            </div>
          </div>
          {loading ? (
            <div className="h-44 flex items-center justify-center text-sm text-slate-400">
              Carregando…
            </div>
          ) : (
            <StatusDonut data={data?.status_breakdown} />
          )}
        </div>
      </div>

      {/* Origem dos candidatos (UTM tracking) */}
      <SourceBreakdownPanel
        endpoint="/dashboard/sources"
        title="Origem dos candidatos"
        description="De onde vieram as candidaturas via páginas públicas"
      />

      {/* Confirmações pendentes (entrevistas aguardando candidato confirmar) */}
      <PendingConfirmationsPanel />

      {/* Bottom row: Recent jobs + Top candidates */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Recent jobs */}
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-lg bg-blue-50 text-brand-blue flex items-center justify-center">
                <Briefcase className="w-4.5 h-4.5" strokeWidth={2.2} />
              </div>
              <div>
                <h2 className="heading text-base font-semibold text-slate-900">
                  Vagas recentes
                </h2>
                <p className="text-xs text-slate-500">Últimas 5 criadas</p>
              </div>
            </div>
            <Link
              to="/vagas"
              data-testid="dashboard-view-all-jobs"
              className="text-xs text-brand-blue hover:underline inline-flex items-center gap-1 font-medium"
            >
              Ver todas <ArrowRight className="w-3 h-3" />
            </Link>
          </div>

          {loading ? (
            <div className="p-6 text-sm text-slate-500">Carregando…</div>
          ) : !data?.recent_jobs?.length ? (
            <div className="p-10 text-center">
              <div className="w-14 h-14 rounded-full bg-blue-50 mx-auto flex items-center justify-center mb-3">
                <Briefcase className="w-6 h-6 text-brand-blue" />
              </div>
              <div className="text-slate-700 text-sm font-medium mb-1">
                Sua primeira vaga aguarda você
              </div>
              <div className="text-slate-500 text-xs mb-4">
                Crie a vaga e suba os currículos — a IA cuida do resto.
              </div>
              <Link
                to="/vagas/nova"
                className="inline-flex items-center gap-2 bg-brand-blue hover:bg-brand-blue-dark text-white px-4 py-2 rounded-md text-sm font-medium"
              >
                <Plus className="w-4 h-4" /> Criar primeira vaga
              </Link>
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {data.recent_jobs.map((j) => (
                <li key={j.id}>
                  <Link
                    to={`/vagas/${j.id}`}
                    data-testid={`dashboard-job-row-${j.id}`}
                    className="flex items-center gap-4 px-5 py-3.5 hover:bg-slate-50 transition-colors group"
                  >
                    <div className="shrink-0 w-9 h-9 rounded-lg bg-gradient-to-br from-blue-100 to-emerald-100 text-brand-blue flex items-center justify-center font-semibold text-sm">
                      {(j.title || "?").trim().charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-slate-900 truncate group-hover:text-brand-blue transition-colors">
                        {j.title}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
                        <span>{j.department || "Sem departamento"}</span>
                        <span className="text-slate-300">•</span>
                        <span className="inline-flex items-center gap-1">
                          <Users className="w-3 h-3" />
                          {j.candidates_count} candidato
                          {j.candidates_count === 1 ? "" : "s"}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <StatusBadge status={j.status} />
                      <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-brand-blue transition-colors" />
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Top candidates */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center">
              <Trophy className="w-4.5 h-4.5" strokeWidth={2.2} />
            </div>
            <div>
              <h2 className="heading text-base font-semibold text-slate-900">
                Top candidatos
              </h2>
              <p className="text-xs text-slate-500">Maior score de match</p>
            </div>
          </div>

          {loading ? (
            <div className="p-6 text-sm text-slate-500">Carregando…</div>
          ) : !data?.top_candidates?.length ? (
            <div className="p-6 text-sm text-slate-500 text-center">
              Faça upload de currículos para ver os destaques aqui.
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {data.top_candidates.map((c, i) => {
                const trophyStyles = [
                  "bg-amber-100 text-amber-700",
                  "bg-slate-100 text-slate-600",
                  "bg-orange-100 text-orange-700",
                ];
                const trophyClass = trophyStyles[i] || "bg-slate-50 text-slate-500";
                return (
                  <li key={c.id} className="px-5 py-3.5">
                    <Link
                      to={`/vagas/${c.job_id}`}
                      data-testid={`dashboard-candidate-${c.id}`}
                      className="flex items-center gap-3 group"
                    >
                      <div
                        className={`shrink-0 w-8 h-8 rounded-full ${trophyClass} flex items-center justify-center text-xs font-bold tabular-nums`}
                      >
                        {i + 1}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-slate-900 truncate group-hover:text-brand-blue transition-colors">
                          {c.name || "—"}
                        </div>
                        <div className="text-xs text-slate-500 truncate">
                          {c.email || "sem e-mail extraído"}
                        </div>
                      </div>
                      <ScoreIndicator score={c.score} compact />
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      {/* Score quality + Funnel summary */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
          <div className="flex items-center gap-2.5 mb-4">
            <div className="w-9 h-9 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center">
              <Award className="w-4.5 h-4.5" strokeWidth={2.2} />
            </div>
            <div>
              <h2 className="heading text-base font-semibold text-slate-900">
                Qualidade do match
              </h2>
              <p className="text-xs text-slate-500">Distribuição por faixa de score</p>
            </div>
          </div>
          {loading ? (
            <div className="text-sm text-slate-500">Carregando…</div>
          ) : (
            <ScoreBars data={data?.score_breakdown || []} />
          )}
        </div>

        <div className="lg:col-span-2 bg-gradient-to-br from-slate-900 via-blue-950 to-emerald-950 rounded-xl p-6 text-white shadow-md relative overflow-hidden">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -right-12 -bottom-12 w-56 h-56 rounded-full bg-emerald-500/20 blur-2xl"
          />
          <div className="relative grid grid-cols-1 sm:grid-cols-3 gap-5">
            <div>
              <div className="text-[11px] uppercase tracking-[0.16em] text-blue-200 mb-1 inline-flex items-center gap-1.5">
                <Clock className="w-3 h-3" /> Pendentes
              </div>
              <div className="heading text-3xl font-bold tabular-nums">
                {loading ? "—" : data?.status_breakdown?.find((s) => s.status === "pendente")?.count ?? 0}
              </div>
              <div className="text-xs text-blue-200/80 mt-1">aguardando análise da IA</div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.16em] text-blue-200 mb-1 inline-flex items-center gap-1.5">
                <CheckCircle2 className="w-3 h-3" /> Aprovados
              </div>
              <div className="heading text-3xl font-bold tabular-nums">
                {loading ? "—" : data?.status_breakdown?.find((s) => s.status === "aprovado")?.count ?? 0}
              </div>
              <div className="text-xs text-blue-200/80 mt-1">prontos para a oferta</div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.16em] text-blue-200 mb-1 inline-flex items-center gap-1.5">
                <TrendingUp className="w-3 h-3" /> Conversão
              </div>
              <div className="heading text-3xl font-bold tabular-nums">
                {loading ? "—" : conversion}
                <span className="text-xl font-semibold opacity-80">%</span>
              </div>
              <div className="text-xs text-blue-200/80 mt-1">
                analisados → contatados
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
